/**
 * Is this cell holding a node number AT ALL, and can it be COMPARED to another
 * row's? (PSTUDIO-3990)
 *
 * One definition, used by everything that has to answer "does this row have a
 * number": the duplicate detector, the missing/invalid error generator, the
 * publish gate, and the error MESSAGES that name a number.
 *
 * Deliberately NOT `toValidNodeNumber` from `paste-node-numbering`. That one
 * answers a different question — "may the paste allocator hand this number
 * out?" — and requires `> 0`. Reusing it here would look like DRY and would be
 * a silent contract break, because a NEGATIVE node number is legal in this
 * product and exists in real data:
 *   - `normalize-node-number.test.ts` pins `normalizeNodeNumber(-1) === -1`
 *     under "returns the value unchanged for VALID integers";
 *   - `smart-node-numbering` Rule 1 exists to "normalize a negative reference to
 *     its positive counterpart" — i.e. precisely because a negative number is
 *     already sitting in the table;
 *   - `prepare-rows-pif-compiler.test.ts` pins that a negative numeric string
 *     reaches the compiler as a negative number;
 *   - the CSV parser's `sanitizeNodeNumber` keeps a leading minus ON PURPOSE.
 * Rejecting negatives would have made every such solution unpublishable — far
 * outside what this ticket asks for.
 *
 * What is NOT an identity: absent, blank (whitespace-only included), a
 * non-integer, `0`, and a value outside the SAFE integer range. Zero is excluded
 * because it is what `sanitizeNodeNumber` returns as its FALLBACK for an empty
 * cell, so a 0 in the data means "no number was read here", not "the user picked
 * node 0" — the very coercion that made a blank cell report as a duplicate of
 * "node number 0". Note that "not an identity" does NOT mean "the cell is
 * empty": `isBlankNodeNumberValue` below is the one predicate that answers that
 * second question, and a 0 is present-but-unusable, never absent.
 *
 * A STRING is parsed by an explicit decimal grammar rather than by `Number()`.
 * `Number()` accepts notations no one types into this column and that no
 * producer of the value emits — `'0x10'` → 16, `'0b101'` → 5, `'1e3'` → 1000 —
 * each of which would silently become a DIFFERENT node number than the cell
 * shows, and could collide with a real one. It also makes the `trim` above
 * load-bearing instead of dead: `Number('  ')` is 0, so a whitespace-only cell
 * used to be rejected by the `num === 0` line in the shape predicate rather than
 * by the blank rule it belongs to. Non-string, non-number inputs (a boolean, an array — both
 * of which `Number()` happily coerces, making `true` "node 1") are not numbers
 * at all and are rejected outright.
 *
 * Magnitude splits this into TWO predicates, because there are two questions
 * with different answers. `toIntegerShapedNodeNumber` asks "is this shaped like
 * a whole number, so that its RANGE has to be checked against the backend's?"
 * and caps nothing; `toNodeNumberIdentity` asks the stricter "…and can it be
 * COMPARED to another cell's number?". The range itself belongs to
 * `normalizeNodeNumber`, the only thing that knows the API's limits and reports
 * them with its own message — its probe lives in `buildJsonFieldErrors`
 * (`json-field-errors.ts`), extracted there from `validateAndSync` so a test can
 * reach it, and it is gated on the SHAPE predicate for the reason spelled out
 * below.
 */
const DECIMAL_INTEGER = /^-?\d+$/;

/**
 * The integer SHAPE, with no bound on magnitude.
 *
 * Its one purpose is to gate the backend range probe. Capping magnitude here
 * would make that probe unreachable for exactly the values it exists to catch,
 * which is why this question is NOT merged into the identity below: hardening
 * one shared predicate for the duplicate contract silently deletes the range
 * check — the same trap `toValidNodeNumber` sets for negatives (it requires
 * `> 0`, so gating on it skips the ceiling test for `-1e19`).
 */
export const toIntegerShapedNodeNumber = (raw: unknown): number | undefined => {
  let num: number;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!DECIMAL_INTEGER.test(trimmed)) return undefined;
    num = Number(trimmed);
  } else if (typeof raw === 'number') {
    num = raw;
  } else {
    return undefined;
  }

  if (!Number.isInteger(num)) return undefined;
  if (num === 0) return undefined;
  return num;
};

/**
 * The identity rows are COMPARED by — the shape above, plus the requirement that
 * the value survive the round trip through a JS number.
 *
 * `Number.isSafeInteger`, not merely `Number.isInteger`: past 2^53 a double can
 * no longer distinguish adjacent integers, so `'9007199254740992'` and
 * `'9007199254740993'` both became 9007199254740992 and such a pair was reported
 * as `Duplicate node number` — two cells highlighted and publishing refused over
 * values that are NOT duplicates, with no second error to explain it. An
 * identity that cannot tell two values apart must not be used to claim they are
 * the same; such a cell falls into the invalid-VALUE branch of
 * `deriveNodeNumberErrors` instead, which is true and still blocks publishing.
 *
 * Consequence to know: a value rejected for magnitude alone can collect that
 * invalid-value error AND, on a server-validation pass, the range error from the
 * probe — two popover rows for one cell. Accepted rather than papered over: the
 * alternative is gating the probe on this predicate, which removes the ceiling
 * check from every value large enough to trip it.
 */
export const toNodeNumberIdentity = (raw: unknown): string | undefined => {
  const num = toIntegerShapedNodeNumber(raw);
  if (num === undefined) return undefined;
  if (!Number.isSafeInteger(num)) return undefined;
  // PSTUDIO-4453: the canonical string a row holds ("007" and "7" are one).
  return String(num);
};

/**
 * Does this cell mean "no node number was entered here", as opposed to "something
 * that is not a node number was"? (PSTUDIO-3990)
 *
 * Both answers make `toNodeNumberIdentity` return undefined, so the identity
 * alone cannot tell them apart — and the two need DIFFERENT wording: an EMPTY
 * cell is missing a number, while a cell showing `'12.5'`, `'abc'` or `0` holds
 * an unusable one. Telling a user who can SEE a value in the cell that their
 * rows have no number is the same class of false claim as the phantom `Node: 0`
 * this ticket removed, one layer up.
 *
 * Blank is therefore exactly: absent (`null` / `undefined`) or a string that is
 * empty or whitespace-only. NOTHING that renders as a visible character is
 * blank.
 *
 * A numeric ZERO is deliberately NOT blank, in any representation (`0`, `'0'`,
 * `'-0'`, `'00'`). It is not a node NUMBER either — it is what
 * `sanitizeNodeNumber` writes as its fallback for a cell it could not read, and
 * what `convertToNumber` (a `parseInt` with an `isNaN → 0` fallback) writes when
 * a paste of 'hello' lands in the column — so `toNodeNumberIdentity` still
 * rejects it and it still blocks publishing. But the cell DISPLAYS that 0, so
 * the honest wording is "this value is not usable", never "there is no number
 * here": the two differ precisely in whether they claim something about a value
 * the user can see.
 *
 * Scope: ONE definition, shared by the publish gate (`findNodeNumberProblem`,
 * which has no cell to point at and must pick one wording for the whole table)
 * and by the per-cell message in `deriveNodeNumberErrors`. They used to disagree
 * on exactly this value — the cell read '"0" is not a valid node number' while
 * the toast said the solution "has rows without a node number" — so whichever
 * the user read second contradicted the first. Sharing the predicate is what
 * makes that impossible rather than merely unlikely.
 */
export const isBlankNodeNumberValue = (raw: unknown): boolean => {
  if (raw === null || raw === undefined) return true;
  return typeof raw === 'string' && raw.trim() === '';
};

/** The node number to put ON a WorkflowError, or null when the cell holds none.
 *  `buildErrorLabel` prints `Node: <n>` for ANY non-nil value, so a raw `0` or
 *  `''` here is exactly how "Node: 0" and the empty "Node: " reached the popover
 *  title — `?? null` does not catch either. */
export const nodeNumberOrNull = (raw: unknown): string | null =>
  toNodeNumberIdentity(raw) ?? null;
